import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getBlogs,
  createBlog,
  updateBlog,
  deleteBlog,
} from "../../feature/blogs/blogThunks";

import {
  selectBlogs,
  selectBlogsLoading,
  selectCreateBlogLoading,
  selectCreateBlogSuccess,
  selectUpdateBlogLoading,
  selectUpdateBlogSuccess,
  selectDeleteBlogLoading,
  selectBlogError,
} from "../../feature/blogs/blogSelector";

import { resetBlogStatus } from "../../feature/blogs/blogSlice";

import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TagIcon from "@mui/icons-material/Tag";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArticleIcon from "@mui/icons-material/Article";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import PublicIcon from "@mui/icons-material/Public";
import ScheduleIcon from "@mui/icons-material/Schedule";

import CommonButton from "../../components/commonComponents/CommonButton";
import CommonToast from "../../components/commonComponents/Toster";
import CommonPagination from "../../components/commonComponents/CommonPagination";
import CommonSearchField from "../../components/commonComponents/CommonSearchField";
import RichTextEditor from "../../components/commonComponents/RichTextEditor";

const API_BASE_URL = "https://apidata.hogonnindia.com";

const getFullImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

const SUGGESTED_HASHTAGS = [
  "#HogoAutofilms",
  "#PaintProtectionFilm",
  "#PPF",
  "#CarCare",
  "#Automotive",
  "#ElectricCars",
  "#EVIndia",
  "#CeramicCoating",
  "#WindowFilm",
];

const generateSlug = (title) => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .substring(0, 80);
};

const Blogs = () => {
  const dispatch = useDispatch();

  const rawBlogsData = useSelector(selectBlogs);
  const loading = useSelector(selectBlogsLoading);
  const createLoading = useSelector(selectCreateBlogLoading);
  const createSuccess = useSelector(selectCreateBlogSuccess);
  const updateLoading = useSelector(selectUpdateBlogLoading);
  const updateSuccess = useSelector(selectUpdateBlogSuccess);
  const deleteLoading = useSelector(selectDeleteBlogLoading);
  const error = useSelector(selectBlogError);

  // Extract blogs array reliably from /blog-data/
  const blogsList = useMemo(() => {
    if (Array.isArray(rawBlogsData?.data)) return rawBlogsData.data;
    if (Array.isArray(rawBlogsData)) return rawBlogsData;
    return [];
  }, [rawBlogsData]);

  // View state
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewBlog, setViewBlog] = useState(null);
  const [deleteDialogItem, setDeleteDialogItem] = useState(null);

  // Filters & Pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [page, setPage] = useState(1);
  const itemsPerPage = 8;

  // Form State
  const initialForm = {
    title: "",
    slug: "",
    short_description: "",
    content: "",
    featured_image: null,
    status: "Published",
    publish_date: new Date().toISOString().slice(0, 10),
    hashtag: "#HogoAutofilms #CarCare",
    meta_title: "",
    meta_descrtiption: "",
    meta_keyword: "",
  };

  const [form, setForm] = useState(initialForm);
  const [previewImage, setPreviewImage] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [errors, setErrors] = useState({});
  const [manualSlug, setManualSlug] = useState(false);

  // Fetch blogs on mount
  const fetchAllBlogs = () => {
    dispatch(getBlogs());
  };

  useEffect(() => {
    fetchAllBlogs();
  }, [dispatch]);

  // Handle create / update success
  useEffect(() => {
    if (createSuccess) {
      CommonToast("Blog published successfully!", "success");
      dispatch(resetBlogStatus());
      handleResetForm();
      fetchAllBlogs();
    }
    if (updateSuccess) {
      CommonToast("Blog updated successfully!", "success");
      dispatch(resetBlogStatus());
      handleResetForm();
      fetchAllBlogs();
    }
  }, [createSuccess, updateSuccess, dispatch]);

  // Filtered and paginated blogs
  const filteredBlogs = useMemo(() => {
    return blogsList.filter((blog) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        blog.title?.toLowerCase().includes(q) ||
        blog.slug?.toLowerCase().includes(q) ||
        blog.short_description?.toLowerCase().includes(q) ||
        blog.hashtag?.toLowerCase().includes(q) ||
        blog.created_at?.includes(q) ||
        String(blog.id).includes(q);

      const matchesStatus =
        selectedStatus === "All" ||
        blog.status?.toLowerCase() === selectedStatus.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [blogsList, searchQuery, selectedStatus]);

  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage) || 1;
  const paginatedBlogs = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredBlogs.slice(start, start + itemsPerPage);
  }, [filteredBlogs, page, itemsPerPage]);

  // Form field handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "title" && !manualSlug) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSlugChange = (e) => {
    setManualSlug(true);
    setForm((prev) => ({ ...prev, slug: e.target.value }));
  };

  const handleAddHashtag = (tag) => {
    setForm((prev) => {
      const current = prev.hashtag ? prev.hashtag.trim() : "";
      if (current.includes(tag)) return prev;
      return {
        ...prev,
        hashtag: current ? `${current} ${tag}` : tag,
      };
    });
    if (errors.hashtag) {
      setErrors((prev) => ({ ...prev, hashtag: "" }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        CommonToast("Please select a valid image file", "error");
        return;
      }
      setForm((prev) => ({ ...prev, featured_image: file }));
      setPreviewImage(URL.createObjectURL(file));
      if (errors.featured_image) {
        setErrors((prev) => ({ ...prev, featured_image: "" }));
      }
    }
  };

  const handleRemoveImage = () => {
    setForm((prev) => ({ ...prev, featured_image: null }));
    setPreviewImage(null);
  };

  const validate = () => {
    const errs = {};
    if (!form.title?.trim()) errs.title = "Title is required";
    if (!form.content?.trim()) errs.content = "Content is required";
    if (!form.hashtag?.trim()) errs.hashtag = "At least one hashtag is required";

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      CommonToast("Please fill all required fields correctly", "error");
    }
    return Object.keys(errs).length === 0;
  };

  // Submit Create or Update
  const handleSubmit = () => {
    if (!validate()) return;

    const formData = new FormData();
    formData.append("title", form.title.trim());
    formData.append("content", form.content);
    formData.append("hashtag", form.hashtag.trim());

    if (form.slug) {
      formData.append("slug", form.slug.trim());
    } else {
      formData.append("slug", generateSlug(form.title));
    }

    if (form.short_description) {
      formData.append("short_description", form.short_description.trim());
    }
    if (form.status) {
      formData.append("status", form.status);
    }
    if (form.publish_date) {
      formData.append("publish_date", form.publish_date);
    }
    if (form.meta_title) {
      formData.append("meta_title", form.meta_title.trim());
    }
    if (form.meta_descrtiption) {
      formData.append("meta_descrtiption", form.meta_descrtiption.trim());
    }
    if (form.meta_keyword) {
      formData.append("meta_keyword", form.meta_keyword.trim());
    }

    // Only append image if a new file was chosen
    if (form.featured_image instanceof File) {
      formData.append("featured_image", form.featured_image);
    }

    if (isEditing && editId) {
      dispatch(updateBlog({ id: editId, data: formData }))
        .unwrap()
        .catch((err) => {
          console.error("Update error:", err);
          CommonToast(
            typeof err === "string" ? err : "Failed to update blog",
            "error",
          );
        });
    } else {
      dispatch(createBlog(formData))
        .unwrap()
        .catch((err) => {
          console.error("Create error:", err);
          CommonToast(
            typeof err === "string" ? err : "Failed to create blog",
            "error",
          );
        });
    }
  };

  // Start Edit
  const handleEdit = (blog) => {
    setEditId(blog.id);
    setIsEditing(true);
    setViewBlog(null);
    setManualSlug(true);
    setForm({
      title: blog.title || "",
      slug: blog.slug || "",
      short_description: blog.short_description || "",
      content: blog.content || "",
      featured_image: null,
      status: blog.status || "Published",
      publish_date: blog.publish_date
        ? blog.publish_date.slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      hashtag: blog.hashtag || "",
      meta_title: blog.meta_title || "",
      meta_descrtiption: blog.meta_descrtiption || "",
      meta_keyword: blog.meta_keyword || "",
    });
    setExistingImageUrl(
      blog.featured_image ? getFullImageUrl(blog.featured_image) : "",
    );
    setPreviewImage(null);
    setErrors({});
  };

  // Delete handler
  const confirmDelete = () => {
    if (!deleteDialogItem) return;
    dispatch(deleteBlog(deleteDialogItem.id))
      .unwrap()
      .then(() => {
        CommonToast("Blog deleted successfully", "success");
        setDeleteDialogItem(null);
        if (viewBlog?.id === deleteDialogItem.id) {
          setViewBlog(null);
        }
        fetchAllBlogs();
      })
      .catch((err) => {
        CommonToast(
          typeof err === "string" ? err : "Failed to delete blog",
          "error",
        );
      });
  };

  // Reset form / go back to list
  const handleResetForm = () => {
    setIsEditing(false);
    setEditId(null);
    setForm(initialForm);
    setPreviewImage(null);
    setExistingImageUrl("");
    setErrors({});
    setManualSlug(false);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // VIEW: CREATE / EDIT FORM
  // ──────────────────────────────────────────────────────────────────────────
  if (isEditing) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", pb: 5 }}>
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          px={3}
          py={2}
          sx={{
            background: "linear-gradient(90deg, #D20000 0%, #8B0000 100%)",
            borderRadius: 2.5,
            boxShadow: "0 4px 16px rgba(210,0,0,0.25)",
            color: "#fff",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton
              onClick={handleResetForm}
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.15)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              }}
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {editId ? `Edit Article #${editId}` : "Create New Blog Article"}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Manage blog publication details on /blog-data/ API
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <CommonButton
              variant="outlined"
              onClick={handleResetForm}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.6)",
                "&:hover": {
                  borderColor: "#fff",
                  bgcolor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="contained"
              onClick={handleSubmit}
              disabled={createLoading || updateLoading}
              sx={{
                bgcolor: "#fff",
                color: "#D20000",
                fontWeight: 700,
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
            >
              {createLoading || updateLoading
                ? "Saving..."
                : editId
                ? "Update Article"
                : "Publish Article"}
            </CommonButton>
          </Stack>
        </Box>

        {/* Form Body */}
        <Grid container spacing={3}>
          {/* Main Column */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={2}
              sx={{
                p: 3.5,
                borderRadius: 3,
                border: "1px solid #eee",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {/* Title */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="#222"
                  gutterBottom
                >
                  Article Title *
                </Typography>
                <TextField
                  name="title"
                  placeholder="e.g. Top 10 Electric Cars in India 2026..."
                  fullWidth
                  value={form.title}
                  onChange={handleInputChange}
                  error={!!errors.title}
                  helperText={errors.title}
                  variant="outlined"
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>

              {/* Slug */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="#222"
                  gutterBottom
                >
                  URL Slug
                </Typography>
                <TextField
                  name="slug"
                  placeholder="top-10-electric-cars-india-2026"
                  fullWidth
                  value={form.slug}
                  onChange={handleSlugChange}
                  variant="outlined"
                  size="small"
                  helperText="Unique URL identifier for the article. Auto-generated from title."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontFamily: "monospace",
                    },
                  }}
                />
              </Box>

              {/* Short Description */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="#222"
                  gutterBottom
                >
                  Short Description
                </Typography>
                <TextField
                  name="short_description"
                  placeholder="Write a brief overview describing what readers will learn..."
                  fullWidth
                  multiline
                  rows={3}
                  value={form.short_description}
                  onChange={handleInputChange}
                  helperText="Displayed in blog listing cards and search snippets."
                  variant="outlined"
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>

              {/* Full Content (Rich Text) */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="#222"
                  gutterBottom
                >
                  Full Article Content * (Rich Text Editor)
                </Typography>
                <RichTextEditor
                  value={form.content}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, content: val }));
                    if (errors.content) {
                      setErrors((prev) => ({ ...prev, content: "" }));
                    }
                  }}
                  error={!!errors.content}
                  helperText={errors.content}
                />
              </Box>

              {/* SEO & Meta Tags Accordion */}
              <Accordion
                sx={{
                  borderRadius: "12px !important",
                  border: "1px solid #e0e0e0",
                  "&:before": { display: "none" },
                  boxShadow: "none",
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PublicIcon sx={{ color: "#D20000", fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      SEO & Meta Settings (Optional)
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1, pb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Meta Title"
                    name="meta_title"
                    placeholder="e.g. Best Electric Vehicles 2026 | Hogo Auto"
                    fullWidth
                    size="small"
                    value={form.meta_title}
                    onChange={handleInputChange}
                  />

                  <TextField
                    label="Meta Description"
                    name="meta_descrtiption"
                    placeholder="SEO description for search engines..."
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    value={form.meta_descrtiption}
                    onChange={handleInputChange}
                  />

                  <TextField
                    label="Meta Keywords"
                    name="meta_keyword"
                    placeholder="e.g. electric cars, EV India, PPF, Hogo Auto"
                    fullWidth
                    size="small"
                    value={form.meta_keyword}
                    onChange={handleInputChange}
                  />
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Grid>

          {/* Sidebar Column */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Featured Image Card */}
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid #eee",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="#222"
                  gutterBottom
                >
                  Featured Image
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={2}
                >
                  Cover photo displayed at the top of the article.
                </Typography>

                {previewImage ? (
                  <Box sx={{ position: "relative", mb: 2 }}>
                    <Box
                      component="img"
                      src={previewImage}
                      alt="Preview"
                      sx={{
                        width: "100%",
                        height: 190,
                        objectFit: "cover",
                        borderRadius: 2,
                        border: "2px solid #D20000",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleRemoveImage}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "rgba(0,0,0,0.65)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgba(210,0,0,0.9)" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography
                      variant="caption"
                      color="success.main"
                      fontWeight={600}
                      display="block"
                      mt={0.5}
                    >
                      New cover photo selected
                    </Typography>
                  </Box>
                ) : existingImageUrl ? (
                  <Box sx={{ position: "relative", mb: 2 }}>
                    <Box
                      component="img"
                      src={existingImageUrl}
                      alt="Current"
                      sx={{
                        width: "100%",
                        height: 190,
                        objectFit: "cover",
                        borderRadius: 2,
                        border: "1px solid #ddd",
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={0.5}
                    >
                      Current cover on server. Upload below to replace.
                    </Typography>
                  </Box>
                ) : null}

                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    py: 1.5,
                    borderStyle: "dashed",
                    borderColor: errors.featured_image ? "#d32f2f" : "#D20000",
                    color: "#D20000",
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: "#8B0000",
                      bgcolor: "rgba(210,0,0,0.04)",
                    },
                  }}
                >
                  {previewImage || existingImageUrl
                    ? "Change Cover Photo"
                    : "Upload Cover Photo"}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
              </Paper>

              {/* Status & Hashtags Card */}
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid #eee",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} color="#222">
                  Publication Settings
                </Typography>

                {/* Status */}
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="#555"
                    display="block"
                    mb={0.5}
                  >
                    Publication Status
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      name="status"
                      value={form.status}
                      onChange={handleInputChange}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="Published">Published</MenuItem>
                      <MenuItem value="Draft">Draft</MenuItem>
                      <MenuItem value="Archived">Archived</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Hashtags */}
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="#555"
                    display="block"
                    mb={0.5}
                  >
                    Hashtags *
                  </Typography>
                  <TextField
                    name="hashtag"
                    placeholder="#HogoAuto #PPF #CarCare"
                    fullWidth
                    size="small"
                    value={form.hashtag}
                    onChange={handleInputChange}
                    error={!!errors.hashtag}
                    helperText={errors.hashtag || "Space-separated hashtags"}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />

                  {/* Suggestion Chips */}
                  <Box display="flex" flexWrap="wrap" gap={0.8} mt={1}>
                    {SUGGESTED_HASHTAGS.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        clickable
                        onClick={() => handleAddHashtag(tag)}
                        sx={{
                          fontSize: "0.72rem",
                          bgcolor: form.hashtag.includes(tag)
                            ? "rgba(210,0,0,0.12)"
                            : "#f5f5f5",
                          color: form.hashtag.includes(tag)
                            ? "#D20000"
                            : "#555",
                          fontWeight: 600,
                          border: form.hashtag.includes(tag)
                            ? "1px solid #D20000"
                            : "1px solid #e0e0e0",
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Publish Date */}
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="#555"
                    display="block"
                    mb={0.5}
                  >
                    Publish Date
                  </Typography>
                  <TextField
                    name="publish_date"
                    type="date"
                    fullWidth
                    size="small"
                    value={form.publish_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // VIEW: MAIN LIST & TABLE
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", pb: 5 }}>
      {/* Title & Action Bar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 5,
              height: 36,
              bgcolor: "#D20000",
              borderRadius: 1,
            }}
          />
          <Box>
            <Typography variant="h5" fontWeight={800} color="#111">
              Blog Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage articles, SEO metadata, and press releases
            </Typography>
          </Box>
        </Box>

        <CommonButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setIsEditing(true);
            setEditId(null);
            setForm(initialForm);
            setPreviewImage(null);
            setExistingImageUrl("");
            setErrors({});
            setManualSlug(false);
          }}
          sx={{
            bgcolor: "#D20000",
            "&:hover": { bgcolor: "#a80000" },
            fontWeight: 700,
            px: 3,
            py: 1.2,
            boxShadow: "0 4px 14px rgba(210,0,0,0.3)",
          }}
        >
          Add New Blog
        </CommonButton>
      </Stack>

      {/* Summary KPI Cards */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={1}
            sx={{
              p: 2.5,
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderLeft: "4px solid #D20000",
            }}
          >
            <Avatar
              sx={{
                bgcolor: "rgba(210,0,0,0.1)",
                color: "#D20000",
                width: 48,
                height: 48,
              }}
            >
              <ArticleIcon />
            </Avatar>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                TOTAL ARTICLES
              </Typography>
              <Typography variant="h5" fontWeight={800} color="#111">
                {blogsList.length}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={1}
            sx={{
              p: 2.5,
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderLeft: "4px solid #2e7d32",
            }}
          >
            <Avatar
              sx={{
                bgcolor: "rgba(46,125,50,0.1)",
                color: "#2e7d32",
                width: 48,
                height: 48,
              }}
            >
              <PublicIcon />
            </Avatar>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                PUBLISHED
              </Typography>
              <Typography variant="h5" fontWeight={800} color="#111">
                {blogsList.filter((b) => b.status === "Published").length}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={1}
            sx={{
              p: 2.5,
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderLeft: "4px solid #ed6c02",
            }}
          >
            <Avatar
              sx={{
                bgcolor: "rgba(237,108,2,0.1)",
                color: "#ed6c02",
                width: 48,
                height: 48,
              }}
            >
              <ScheduleIcon />
            </Avatar>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                FILTERED ARTICLES
              </Typography>
              <Typography variant="h5" fontWeight={800} color="#111">
                {filteredBlogs.length}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Main Table Card */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
        {/* Search & Filter Toolbar */}
        <Box
          sx={{
            p: 2.5,
            bgcolor: "#fafafa",
            borderBottom: "1px solid #eee",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
          }}
        >
          <Box sx={{ width: { xs: "100%", md: 360 } }}>
            <CommonSearchField
              value={searchQuery}
              placeholder="Search by title, slug, hashtag..."
              onChange={(val) => {
                setSearchQuery(val);
                setPage(1);
              }}
            />
          </Box>

          {/* Status Filters */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              STATUS:
            </Typography>
            {["All", "Published", "Draft"].map((st) => (
              <Chip
                key={st}
                label={st}
                size="small"
                clickable
                onClick={() => {
                  setSelectedStatus(st);
                  setPage(1);
                }}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  bgcolor: selectedStatus === st ? "#D20000" : "#fff",
                  color: selectedStatus === st ? "#fff" : "#444",
                  border:
                    selectedStatus === st
                      ? "1px solid #D20000"
                      : "1px solid #ddd",
                  "&:hover": {
                    bgcolor: selectedStatus === st ? "#a80000" : "#f5f5f5",
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Table Content */}
        <TableContainer sx={{ minHeight: 340 }}>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow
                sx={{
                  background:
                    "linear-gradient(90deg, #D20000 0%, #8B0000 100%)",
                }}
              >
                {[
                  "Sr",
                  "Cover",
                  "Title & Slug",
                  "Hashtags & Status",
                  "Summary",
                  "Date",
                  "Actions",
                ].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: 700,
                      color: "#fff",
                      py: 1.8,
                      fontSize: "0.88rem",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress sx={{ color: "#D20000" }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mt={1.5}
                      fontWeight={500}
                    >
                      Loading blogs from server...
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading && paginatedBlogs.length > 0 && (
                paginatedBlogs.map((blog, idx) => {
                  const serialNumber = (page - 1) * itemsPerPage + idx + 1;
                  const imgUrl = getFullImageUrl(blog.featured_image);

                  return (
                    <TableRow
                      key={blog.id}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        transition: "background-color 0.2s ease",
                      }}
                    >
                      {/* Sr */}
                      <TableCell sx={{ fontWeight: 600, color: "#666", width: 50 }}>
                        {serialNumber}
                      </TableCell>

                      {/* Image Thumbnail */}
                      <TableCell sx={{ width: 90 }}>
                        <Avatar
                          src={imgUrl}
                          alt={blog.title}
                          variant="rounded"
                          sx={{
                            width: 64,
                            height: 48,
                            bgcolor: "#eee",
                            border: "1px solid #ddd",
                            cursor: "pointer",
                            transition: "transform 0.2s",
                            "&:hover": { transform: "scale(1.08)" },
                          }}
                          onClick={() => setViewBlog(blog)}
                        >
                          <ArticleIcon sx={{ color: "#999" }} />
                        </Avatar>
                      </TableCell>

                      {/* Title & Slug */}
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          color="#111"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: 1.3,
                            mb: 0.5,
                          }}
                        >
                          {blog.title}
                        </Typography>
                        {blog.slug && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#666",
                              bgcolor: "#f0f0f0",
                              px: 0.8,
                              py: 0.2,
                              borderRadius: 1,
                              fontFamily: "monospace",
                              display: "inline-block",
                            }}
                          >
                            /{blog.slug}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Hashtags & Status */}
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Stack spacing={0.6}>
                          <Chip
                            label={blog.status || "Published"}
                            size="small"
                            sx={{
                              width: "fit-content",
                              height: 20,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              bgcolor:
                                blog.status === "Draft"
                                  ? "rgba(237,108,2,0.12)"
                                  : "rgba(46,125,50,0.12)",
                              color:
                                blog.status === "Draft"
                                  ? "#ed6c02"
                                  : "#2e7d32",
                            }}
                          />
                          {blog.hashtag && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                lineHeight: 1.2,
                                color: "#1976d2",
                                fontWeight: 500,
                              }}
                            >
                              {blog.hashtag}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      {/* Short Description */}
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: 1.4,
                          }}
                        >
                          {blog.short_description || "—"}
                        </Typography>
                      </TableCell>

                      {/* Date */}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Box display="flex" alignItems="center" gap={0.8}>
                          <CalendarTodayIcon
                            fontSize="small"
                            sx={{ color: "#888", fontSize: 16 }}
                          />
                          <Typography variant="body2" fontWeight={600} color="#333">
                            {blog.created_at || blog.publish_date?.slice(0, 10) || "—"}
                          </Typography>
                        </Box>
                        {blog.updated_at && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mt={0.2}
                          >
                            Updated: {blog.updated_at}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ width: 140 }}>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="View Article Details">
                            <IconButton
                              size="small"
                              onClick={() => setViewBlog(blog)}
                              sx={{
                                color: "#1976d2",
                                bgcolor: "rgba(25,118,210,0.08)",
                                "&:hover": { bgcolor: "rgba(25,118,210,0.18)" },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Edit Article (PATCH)">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(blog)}
                              sx={{
                                color: "#f57c00",
                                bgcolor: "rgba(245,124,0,0.08)",
                                "&:hover": { bgcolor: "rgba(245,124,0,0.18)" },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Article (DELETE)">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialogItem(blog)}
                              sx={{
                                color: "#d32f2f",
                                bgcolor: "rgba(211,47,47,0.08)",
                                "&:hover": { bgcolor: "rgba(211,47,47,0.18)" },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}

              {!loading && paginatedBlogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: "#f5f5f5",
                          color: "#999",
                        }}
                      >
                        <ArticleIcon sx={{ fontSize: 32 }} />
                      </Avatar>
                      <Typography variant="subtitle1" fontWeight={700} color="#444">
                        No articles found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchQuery || selectedStatus !== "All"
                          ? "Try adjusting your search criteria."
                          : "Get started by creating your first blog article."}
                      </Typography>
                      {!searchQuery && selectedStatus === "All" && (
                        <CommonButton
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            setIsEditing(true);
                            setEditId(null);
                            setForm(initialForm);
                          }}
                          sx={{
                            mt: 1,
                            bgcolor: "#D20000",
                            "&:hover": { bgcolor: "#a80000" },
                          }}
                        >
                          Create First Blog
                        </CommonButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Bar */}
        <CommonPagination
          count={totalPages}
          page={page}
          onPageChange={setPage}
          totalItems={filteredBlogs.length}
          rowsPerPage={itemsPerPage}
          itemName="articles"
        />
      </Paper>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* MODAL: VIEW BLOG ARTICLE DETAILS                                   */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <Dialog
        open={Boolean(viewBlog)}
        onClose={() => setViewBlog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: "hidden" },
        }}
      >
        {viewBlog && (
          <>
            {/* Cover Banner */}
            <Box sx={{ position: "relative", bgcolor: "#111", height: 260 }}>
              {viewBlog.featured_image ? (
                <Box
                  component="img"
                  src={getFullImageUrl(viewBlog.featured_image)}
                  alt={viewBlog.title}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: 0.9,
                  }}
                />
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  bgcolor="#222"
                >
                  <ArticleIcon sx={{ fontSize: 64, color: "#555" }} />
                </Box>
              )}

              {/* Close Button */}
              <IconButton
                onClick={() => setViewBlog(null)}
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  bgcolor: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.85)" },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Status & Hashtags Chip Overlay */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 20,
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Chip
                  label={viewBlog.status || "Published"}
                  sx={{
                    bgcolor:
                      viewBlog.status === "Draft" ? "#ed6c02" : "#2e7d32",
                    color: "#fff",
                    fontWeight: 700,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                  }}
                />
                {viewBlog.hashtag && (
                  <Chip
                    label={viewBlog.hashtag}
                    sx={{
                      bgcolor: "rgba(0,0,0,0.65)",
                      color: "#fff",
                      backdropFilter: "blur(4px)",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            </Box>

            <DialogContent sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight={800} color="#111" gutterBottom>
                {viewBlog.title}
              </Typography>

              {/* Metadata Details */}
              <Box
                display="flex"
                alignItems="center"
                gap={3}
                flexWrap="wrap"
                color="text.secondary"
                fontSize="0.85rem"
                mb={2.5}
              >
                {viewBlog.slug && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <strong>Slug:</strong> /{viewBlog.slug}
                  </Box>
                )}
                {viewBlog.created_at && (
                  <Box display="flex" alignItems="center" gap={0.6}>
                    <CalendarTodayIcon fontSize="inherit" />
                    <span>Created: {viewBlog.created_at}</span>
                  </Box>
                )}
                {viewBlog.updated_at && (
                  <span>Updated: {viewBlog.updated_at}</span>
                )}
                <span>Article ID: #{viewBlog.id}</span>
              </Box>

              <Divider sx={{ mb: 2.5 }} />

              {/* Short summary block */}
              {viewBlog.short_description && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(210,0,0,0.04)",
                    borderLeft: "4px solid #D20000",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="#D20000"
                    display="block"
                    mb={0.5}
                  >
                    OVERVIEW
                  </Typography>
                  <Typography variant="body2" color="#333" fontStyle="italic">
                    {viewBlog.short_description}
                  </Typography>
                </Box>
              )}

              {/* Full Content */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="#222"
                  mb={1}
                >
                  Full Content:
                </Typography>
                <Box
                  sx={{
                    typography: "body1",
                    color: "#333",
                    lineHeight: 1.7,
                    "& img": { maxWidth: "100%", borderRadius: 1.5 },
                    "& p": { mb: 1.5 },
                    "& h2": { mt: 2.5, mb: 1, fontWeight: 700 },
                    "& h3": { mt: 2, mb: 1, fontWeight: 600 },
                    "& a": { color: "#D20000" },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: viewBlog.content || "<i>No content provided.</i>",
                  }}
                />
              </Box>

              {/* SEO preview if present */}
              {(viewBlog.meta_title || viewBlog.meta_descrtiption || viewBlog.meta_keyword) && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f9f9f9",
                    border: "1px solid #eee",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} color="#666" display="block" mb={0.5}>
                    SEO METADATA
                  </Typography>
                  {viewBlog.meta_title && (
                    <Typography variant="caption" display="block">
                      <strong>Meta Title:</strong> {viewBlog.meta_title}
                    </Typography>
                  )}
                  {viewBlog.meta_descrtiption && (
                    <Typography variant="caption" display="block">
                      <strong>Meta Description:</strong> {viewBlog.meta_descrtiption}
                    </Typography>
                  )}
                  {viewBlog.meta_keyword && (
                    <Typography variant="caption" display="block">
                      <strong>Meta Keywords:</strong> {viewBlog.meta_keyword}
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>

            <DialogActions
              sx={{
                p: 2.5,
                bgcolor: "#fafafa",
                borderTop: "1px solid #eee",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <CommonButton
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  const toDelete = viewBlog;
                  setViewBlog(null);
                  setDeleteDialogItem(toDelete);
                }}
              >
                Delete
              </CommonButton>

              <Stack direction="row" spacing={1.5}>
                <CommonButton
                  variant="outlined"
                  onClick={() => setViewBlog(null)}
                >
                  Close
                </CommonButton>
                <CommonButton
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => handleEdit(viewBlog)}
                  sx={{
                    bgcolor: "#D20000",
                    "&:hover": { bgcolor: "#a80000" },
                  }}
                >
                  Edit Article
                </CommonButton>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* MODAL: DELETE CONFIRMATION                                          */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <Dialog
        open={Boolean(deleteDialogItem)}
        onClose={() => !deleteLoading && setDeleteDialogItem(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <WarningAmberIcon color="error" sx={{ fontSize: 32 }} />
          <Typography variant="h6" fontWeight={700}>
            Delete Blog Post?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete{" "}
            <strong>"{deleteDialogItem?.title}"</strong>? This will execute a
            DELETE operation on the server and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CommonButton
            variant="outlined"
            onClick={() => setDeleteDialogItem(null)}
            disabled={deleteLoading}
          >
            Cancel
          </CommonButton>
          <CommonButton
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleteLoading}
            sx={{ bgcolor: "#d32f2f", "&:hover": { bgcolor: "#b71c1c" } }}
          >
            {deleteLoading ? "Deleting..." : "Delete Permanently"}
          </CommonButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Blogs;
